"use client";

import { Compass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
export default function Environment() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const divider = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const disciplines = [{
    number: "01",
    title: "Practical Life",
    description: "Children develop independence and coordination through purposeful tasks like pouring, cleaning, and dressing, building the foundation for all future learning.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/practical_life_activity.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/practical_life_activity.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/practical_life_activity.png-wvc-sizes",
    alt: "Child carefully pouring water in a Montessori classroom",
    span: "lg:col-span-5",
    height: "lg:h-[520px]",
    offset: ""
  }, {
    number: "02",
    title: "Sensorial Exploration",
    description: "Refining the senses through tactile materials that isolate specific qualities like size, color, and texture, fostering logical thinking and classification skills.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/sensorial_materials.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/sensorial_materials.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/sensorial_materials.png-wvc-sizes",
    alt: "Montessori sensorial materials arranged on a wooden shelf",
    span: "lg:col-span-4",
    height: "lg:h-[420px]",
    offset: "lg:mt-24"
  }, {
    number: "03",
    title: "Language & Culture",
    description: "An immersive environment where phonetic awareness, vocabulary expansion, and cultural studies bloom naturally through exploration and storytelling.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/language_learning.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/language_learning.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/language_learning.png-wvc-sizes",
    alt: "Child engaging with sandpaper letters in a bright classroom",
    span: "lg:col-span-3",
    height: "lg:h-[580px]",
    offset: ""
  }];
  return <section data-section-id="3193" id="the-montessori-environment" className="bg-background text-foreground py-24 md:py-32 lg:py-[180px] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end mb-20 md:mb-28">
          <div ref={header.ref} className={`lg:col-span-7 transition-all duration-[900ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-muted-foreground mb-8">
              Our Approach — Curriculum
            </p>
            <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[72px] leading-[1.05] tracking-tight text-foreground">
              A prepared environment,
              <br />
              <span className="font-serif italic text-primary/90">
                nurturing every seed.
              </span>
            </h2>
          </div>

          <div ref={quote.ref} className={`lg:col-span-5 lg:text-right transition-all duration-[1100ms] delay-200 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-serif italic text-xl md:text-2xl leading-snug text-muted-foreground max-w-xs lg:ml-auto">
              "The child is a source of love; when one touches a child, one touches love."
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {disciplines.map((item, i) => {
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} ref={card.ref} key={i} className={`group ${item.span} ${item.offset} transition-all duration-[900ms] ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <article className={`relative h-full ${item.height} bg-card text-card-foreground border border-border rounded-lg shadow-md overflow-hidden flex flex-col transition-all duration-500 ease-out group-hover:-translate-y-1 group-hover:shadow-lg`}>
                  <div className="overflow-hidden">
                    <AspectRatio ratio={4 / 5}>
                      <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.02]" />
                    </AspectRatio>
                  </div>

                  <div className="flex flex-col flex-1 p-6 md:p-8">
                    <div className="h-px w-full bg-border mb-5" />
                    <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">
                      {item.number} / 03
                    </p>
                    <h3 className="relative font-default font-semibold text-2xl md:text-[28px] leading-tight text-foreground mb-3 inline-block w-fit">
                      {item.title}
                      <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
                    </h3>
                    <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </article>
              </div>;
        })}
        </div>

        {}
        <div ref={divider.ref} className={`mt-24 md:mt-32 flex items-center gap-6 transition-all duration-[1000ms] ease-out ${divider.isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="h-px flex-1 bg-border" />
          <Compass className="size-5 text-chart-3" strokeWidth={1.25} aria-hidden="true" />
          <div className="h-px flex-1 bg-border" />
        </div>
      </div>
    </section>;
}